import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Card, Typography, Tabs, Button, Space, Row, Col, Alert, Select, Input,
  Switch, Table, Tag, App, Empty, Tooltip, Modal, Form, Checkbox, Divider,
} from 'antd';
import {
  ShopOutlined, SearchOutlined, ReloadOutlined, LinkOutlined,
  ArrowUpOutlined, ArrowDownOutlined, VerticalAlignTopOutlined,
  DeleteOutlined, DollarOutlined, EyeOutlined, SaveOutlined, PlusOutlined,
  BankOutlined, EditOutlined, LayoutOutlined,
  BgColorsOutlined, FontSizeOutlined, ContactsOutlined, TeamOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import PujaSpinner from '../../components/PujaSpinner';
// Los tabs del micrositio de remate, REUSADOS SIN TOCARLOS. Lo que los hace
// reusables no es la cantidad de props sino que NINGUNO depende de `evento`:
// la base es `{ config, onChange }` y algunos suman props de inyección
// (`ocultar` en `EstructuraTab`, `subirImagen`/`eliminarImagen` en
// `FondoHeroTab`) justamente para no acoplarse. Además,
// `DEFAULT_CATALOGO_CONFIG` comparte deliberadamente sus nombres de clave con
// `DEFAULT_MICROSITIO_CONFIG`. Bifurcarlos sería mantener dos copias de la
// misma pantalla y arreglar cada bug de color o tipografía dos veces.
import EstructuraTab from '../Herramientas/components/EstructuraTab';
import ColoresTab from '../Herramientas/components/ColoresTab';
import TipografiasTab from '../Herramientas/components/TipografiasTab';
import ContactoTab from '../Herramientas/components/ContactoTab';
import FondoHeroTab from '../Herramientas/components/FondoHeroTab';
// Lo PROPIO del catálogo: la plantilla del index y las dos secciones de
// animales. No existe en un remate, así que no había de dónde reusarlo.
import EstructuraCatalogoTab from './components/catalogo/EstructuraCatalogoTab';
import ModalPresentacionFotos from './components/catalogo/ModalPresentacionFotos';
import ContenidosTab from './components/catalogo/ContenidosTab';
import {
  catalogoGeneticaService, razaService, categoriaService, animalService, fixUrl,
} from '../../services/api';
import { portadaDe } from '../../utils/portadaAnimal';

const { Title, Text, Paragraph } = Typography;

// Cuántos candidatos por página. La paginación es del SERVIDOR: el padrón de
// una cabaña grande pasa los 600 animales y traerlos todos para filtrar en el
// browser es exactamente el problema que `vista=ids` vino a sacar de encima.
const PER_PAGE = 50;

// Tabs que van a pantalla completa, sin nada al lado.
//
// ACA LA LEE UN SOLO CONSUMIDOR: `esTabAncho`, y con el la visibilidad de la
// barra de guardado. No hay split de columna en esta pantalla —el Tabs ocupa
// la Card entera—, asi que la lista existe para no repetir la condicion el dia
// que sean tres tabs, no para sincronizar dos lugares.
//
// `Micrositio.jsx` tenia su gemela, `TABS_FULL_WIDTH`, para el ancho de la
// columna Y para ocultar el preview que iba al lado. Se borro el 2026-10-14
// junto con ese preview: alla ahora TODAS las tabs son anchas y el preview es
// un modal. Si buscas esa constante, ya no esta.
// `metricas` salio de aca junto con su tab: las metricas del catalogo se miran
// en la pantalla de Analytics, que es el unico lugar donde se miran numeros.
const TABS_ANCHOS = ['animales'];

// Tope por cabaña en la rama del catálogo AGRUPADO, donde se pide cada cabaña
// por separado y se concatena. Existe porque `GET /animales` filtra por UNA
// cabaña y no por un conjunto; cuando se alcanza, se avisa en pantalla.
const TOPE_POR_CABANA = 200;

/**
 * El input del nombre corto dentro de una fila.
 *
 * Estado LOCAL mientras se escribe y guardado al salir del campo. Sin estado
 * propio, cada tecla tendría que subir al padre y volver, y el re-render de la
 * tabla entera le come el foco al input a la mitad de la palabra.
 *
 * A nivel de módulo: definido dentro del render de la pantalla, React lo ve
 * como un componente nuevo en cada cambio y remonta todos los inputs de la
 * tabla — con el mismo resultado.
 */
const NombreCortoInput = ({ animal, valor, onGuardar }) => {
  const [texto, setTexto] = useState(valor || '');

  // La fila puede cambiar de animal sin desmontarse (paginación, filtros), y
  // ahí `useState(valor)` seguiría mostrando el del animal anterior.
  useEffect(() => { setTexto(valor || ''); }, [valor, animal.id_animal]);

  return (
    <Input
      size="small"
      value={texto}
      maxLength={80}
      placeholder="Ej: PAYESERO"
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => onGuardar(animal, texto)}
      onPressEnter={(e) => e.target.blur()}
    />
  );
};

// Columnas compartidas por las DOS tablas. Es una función y no una constante
// porque cada pestaña agrega columnas propias al final, pero el tronco tiene
// que ser el mismo: si las dos listas del mismo animal mostraran datos
// distintos, el productor no podría comparar lo que eligió con lo que le queda.
//
// `mostrarCabana` sólo cuando el catálogo agrupa más de una: en el caso normal
// —una sola cabaña— la columna repetiría el mismo texto en todas las filas.
const columnasBase = (mostrarCabana, onNombreCorto, onAbrirFotos, puedeFotos) => [
  {
    title: '',
    dataIndex: 'imagenes',
    width: 56,
    render: (imagenes) => {
      // LA PORTADA, con el MISMO criterio que el backend (`_portada_de`): la
      // marcada, y si no hay ninguna, la primera. Decia "todavia no existe
      // forma de elegirla (falta la columna `es_principal`)" — ya existe, se
      // llama `es_portada`. Dos criterios distintos harian que el productor
      // marque una portada, vea otra en el panel, y no entienda cual se publica.
      //
      // `fixUrl` NO es opcional: el backend devuelve RUTAS RELATIVAS
      // (`/static/uploads/...`), y sin resolverlas contra su origen el browser
      // las busca en el del frontend y recibe el SPA fallback —un 200 con
      // text/html— en vez de la imagen. O sea: no falla, muestra una foto rota.
      const portada = portadaDe(imagenes);
      const url = fixUrl(portada?.url_thumb);
      return url
        ? (
          <img
            src={url}
            alt=""
            style={{
              width: 40, height: 40, objectFit: 'cover', borderRadius: 4,
              // Con el encuadre elegido, para que la miniatura muestre lo mismo
              // que va a mostrar el catalogo.
              objectPosition: `${portada?.foco_x ?? 50}% ${portada?.foco_y ?? 50}%`,
            }}
          />
        )
        : <div style={{ width: 40, height: 40, borderRadius: 4, background: '#f0f0f0' }} />;
    },
  },
  { title: 'Corral', dataIndex: 'corral', width: 80, render: (v) => v ?? '—' },
  // 80 y no 110: un RP es "389M" o un numero de cinco cifras. La columna medía
  // mas del doble de lo que jamas va a contener.
  { title: 'RP', dataIndex: 'rp_animal', width: 80, render: (v) => v || '—' },
  {
    // EL NOMBRE Y SUS TRES DATOS DE CONTEXTO, en la misma celda. Cabaña,
    // categoria y raza eran tres columnas de 450px combinados, y en una tabla
    // que ya se iba de ancho eran justamente las tres que no se leen de a una:
    // se leen JUNTO al animal, para saber de quien es y que es.
    title: 'Nombre',
    dataIndex: 'nom_animal',
    render: (v, animal) => (
      <div style={{ minWidth: 0 }}>
        <div>{v || <Text type="secondary">Sin nombre</Text>}</div>
        <Space size={[4, 4]} wrap style={{ marginTop: 4 }}>
          {/* La cabaña solo cuando el catalogo agrupa mas de una: con una sola
              el chip repetiria el mismo texto en todas las filas. Mismo
              criterio que tenia la columna. */}
          {mostrarCabana && animal.cabana?.nom_cabana && (
            <Tag style={{ marginInlineEnd: 0 }}>{animal.cabana.nom_cabana}</Tag>
          )}
          {animal.categoria?.nom_categoria && (
            <Tag style={{ marginInlineEnd: 0 }}>{animal.categoria.nom_categoria}</Tag>
          )}
          {animal.nom_raza && (
            <Tag style={{ marginInlineEnd: 0 }}>{animal.nom_raza}</Tag>
          )}
        </Space>
      </div>
    ),
  },
  {
    // El nombre de FANTASÍA: es lo que sale en el menú del catálogo público y
    // en las tarjetas. `nom_animal` es el de pedigrí completo —"PAYESERO
    // MARCABOTE 51M PAYÉ 21570 MÁGICO T/E"— y no entra en una barra de menú.
    //
    // Se edita ACÁ y no sólo en la ficha porque cargarlo de a uno son 300 idas
    // y vueltas para un campo de una palabra.
    title: 'Nombre corto',
    dataIndex: 'nombre_corto',
    width: 150,
    render: (valor, animal) => (
      <NombreCortoInput animal={animal} valor={valor} onGuardar={onNombreCorto} />
    ),
  },
  {
    // COMO SE MUESTRAN LAS FOTOS de este animal: cual es la portada, cuales van
    // al carrusel y por donde se recorta cada una. Va detras de un boton y no
    // inline porque un animal puede tener cinco fotos y sus controles no entran
    // en una celda.
    title: 'Presentación',
    key: 'presentacion',
    width: 120,
    render: (_, animal) => {
      const cuantas = (animal.imagenes || []).length;
      // TERCER PERMISO DE ESTA PANTALLA, y hay que mirarlo aca. La pantalla se
      // gatea con `catalogo_genetica`, pero el modal ESCRIBE con
      // `PUT /imagenes/:id`, que pide `fotos_videos`. Sin esta guarda el
      // productor ve el boton activo, abre el modal, toca "Portada" y recien
      // ahi se come un 403 — el mismo modo de falla que ya tiene documentado
      // el editor de "Nombre corto" con `crud_animales`.
      const sinPermiso = !puedeFotos;
      const titulo = sinPermiso
        ? 'Tu plan no incluye la gestión de fotos'
        : (cuantas === 0 ? 'Este animal no tiene fotos cargadas' : 'Portada, carrusel y encuadre');
      return (
        <Tooltip title={titulo}>
          <span>
            <Button
              size="small"
              icon={<PictureOutlined />}
              onClick={() => onAbrirFotos(animal)}
              // Sin fotos no hay nada que presentar. Un boton que abre un modal
              // vacio promete algo que no esta.
              disabled={cuantas === 0 || sinPermiso}
            >
              {cuantas === 0 ? 'Sin fotos' : cuantas}
            </Button>
          </span>
        </Tooltip>
      );
    },
  },
];


// ──────────────────────────────────────────────────────────────────────────────
// Modal de alta / edición de cabañas del catálogo
// ──────────────────────────────────────────────────────────────────────────────

const ModalCatalogo = ({ abierto, onCerrar, onListo, catalogo, cabanas }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [guardando, setGuardando] = useState(false);
  const esEdicion = Boolean(catalogo);

  useEffect(() => {
    if (!abierto) return;
    form.setFieldsValue({
      nombre_catalogo: catalogo?.nombre_catalogo || '',
      cabanas: catalogo?.cabanas?.map((c) => c.id_cabana) || [],
    });
  }, [abierto, catalogo, form]);

  // Una cabaña se puede elegir si está libre O si ya es de ESTE catálogo.
  // El segundo caso importa: sin él, editar un catálogo mostraría sus propias
  // cabañas como no disponibles.
  const opciones = cabanas.map((cab) => {
    const libre = cab.id_catalogo_genetica == null;
    const mia = catalogo && cab.id_catalogo_genetica === catalogo.id_catalogo_genetica;
    return {
      value: cab.id_cabana,
      label: cab.nom_cabana,
      disabled: !libre && !mia,
    };
  });

  const enviar = async () => {
    let valores;
    try {
      valores = await form.validateFields();
    } catch {
      // Se traga a proposito: antd ya pinta el error debajo de cada campo, y un
      // toast encima seria decir dos veces lo mismo. La excepcion va comentada
      // porque sin comentario esto es un catch vacio y punto.
      return;
    }
    setGuardando(true);
    // Se refresca SIEMPRE al salir, haya ido bien o mal. Son dos llamadas
    // separadas: si `setCabanas` funciona y `update` falla, la composición YA
    // cambió en la base y sin este refresh el panel seguiría mostrando las
    // cabañas viejas. Un estado a medio aplicar hay que mostrarlo, no esconderlo
    // — que se vea es justamente para lo que se eligió este orden de llamadas.
    let idNuevo = null;   // el catálogo recién creado, entero
    try {
      if (esEdicion) {
        // Dos llamadas porque son dos recursos distintos: el nombre es del
        // catálogo y las cabañas son su composición. Primero el conjunto de
        // cabañas —que es lo que puede fallar por estar tomada— así un 409 no
        // deja el nombre cambiado sobre una composición vieja.
        await catalogoGeneticaService.setCabanas(
          catalogo.id_catalogo_genetica, valores.cabanas,
        );
        await catalogoGeneticaService.update(
          catalogo.id_catalogo_genetica,
          { nombre_catalogo: valores.nombre_catalogo },
        );
        message.success('Catálogo actualizado');
      } else {
        const { data } = await catalogoGeneticaService.create(valores);
        // Viaja el catálogo ENTERO, no sólo el id. El POST ya devuelve todo
        // —config incluida— así que la pantalla se puede pintar al instante en
        // vez de esperar dos GET más. Ver la nota de `cargarCatalogos`.
        idNuevo = data?.catalogo ?? null;
        message.success('Catálogo creado');
      }
      onCerrar();
    } catch (e) {
      // El backend redacta el motivo (plan agotado, cabaña tomada, ajena): se
      // muestra tal cual en vez de reescribirlo acá, que es donde menos se sabe.
      message.error(e.response?.data?.error || 'No se pudo guardar');
    } finally {
      setGuardando(false);
      onListo(idNuevo);
    }
  };

  return (
    <Modal
      open={abierto}
      onCancel={onCerrar}
      onOk={enviar}
      okText={esEdicion ? 'Guardar' : 'Crear catálogo'}
      cancelText="Cancelar"
      confirmLoading={guardando}
      title={esEdicion ? 'Editar catálogo' : 'Nuevo catálogo'}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="nombre_catalogo"
          label="Nombre"
          extra="Cómo lo vas a reconocer en este panel. El título de la página se configura aparte."
          rules={[{ required: true, message: 'Poné un nombre' }]}
        >
          <Input placeholder="Ej: Pilagá" />
        </Form.Item>

        <Form.Item
          name="cabanas"
          label="Cabañas que publica"
          extra="Podés juntar varias en un solo sitio. Una cabaña sólo puede estar en un catálogo."
          rules={[{ required: true, message: 'Elegí al menos una cabaña' }]}
        >
          <Checkbox.Group style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {opciones.map((o) => (
              <Checkbox key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
                {o.disabled && (
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                    (ya está en otro catálogo)
                  </Text>
                )}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </Form.Item>
      </Form>
    </Modal>
  );
};


// ──────────────────────────────────────────────────────────────────────────────
// Pantalla
// ──────────────────────────────────────────────────────────────────────────────

const CatalogoGenetica = () => {
  const { message, modal } = App.useApp();
  // `tieneRecurso` sale de `useAuth` y NO de `usePermisos`, aunque los dos
  // contextos lo expongan. `ProtectedRoute` (App.jsx) y el menú (MainLayout)
  // ya usan éste, y dos fuentes de verdad para la misma pregunta significa que
  // el día que una se refresque y la otra no, el menú ofrece la opción y la
  // pantalla contesta "no tenés acceso".
  const { user, tieneRecurso } = useAuth();

  // El id del tenant. Para el admin de plataforma es null, y ahí NO se filtra
  // — igual que hace `solo_propios()` en el backend vía `es_admin_plataforma()`.
  const idCliente = user?.id_clientes ?? null;

  // ── Los catálogos del tenant ────────────────────────────────────────────
  // El animal cuyo modal de fotos esta abierto, o null. Se guarda el ANIMAL y
  // no un booleano + un id: el modal necesita su nombre para el titulo, y
  // buscarlo de nuevo en la lista para eso es trabajo de mas.
  const [animalFotos, setAnimalFotos] = useState(null);
  const [catalogos, setCatalogos] = useState([]);
  const [cabanas, setCabanas] = useState([]);
  const [limite, setLimite] = useState(null);
  const [cargando, setCargando] = useState(true);
  // La carga inicial desmonta la pantalla entera (`PujaSpinner`); ésta no.
  // Un refresh después de guardar tiene que avisar que está pasando algo SIN
  // hacer desaparecer lo que el productor está mirando.
  const [refrescando, setRefrescando] = useState(false);
  const [idActivo, setIdActivo] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editando, setEditando] = useState(null);

  // ── Configuración del catálogo activo ───────────────────────────────────
  const [config, setConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [whatsapp, setWhatsapp] = useState('');
  // Default visual 'whatsapp' cuando el catálogo no trae el campo todavía
  // (API vieja, o un catálogo creado antes de este cambio): es el
  // comportamiento que ya tenía, así que no puede mutar solo por refrescar.
  const [canalConsultas, setCanalConsultas] = useState('whatsapp');

  // La config del SITIO (colores, tipografías, cabecera, hero, secciones) se
  // edita en memoria y se guarda con un botón, igual que en `Micrositio.jsx`.
  // NO se guarda campo por campo como `activo` o el WhatsApp: arrastrar el
  // slider del logo dispararía un PUT por cada pixel.
  const [configSitio, setConfigSitio] = useState({});
  const [sitioSucio, setSitioSucio] = useState(false);
  // APARTE de `configSitio`: `plantilla_index` es una columna del modelo, no
  // una clave del JSON. Viaja como campo de primer nivel en el PUT.
  const [plantilla, setPlantilla] = useState('op1');

  // ── Pestaña "Todos mis animales" ────────────────────────────────────────
  const [candidatos, setCandidatos] = useState([]);
  const [totalCandidatos, setTotalCandidatos] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [loadingCand, setLoadingCand] = useState(false);
  const [seleccion, setSeleccion] = useState([]);
  const [marcando, setMarcando] = useState(false);
  // Qué animal se está escribiendo AHORA. Con `marcando` a secas, tocar el
  // switch de una fila ponía a girar el de todas las demás.
  const [marcandoId, setMarcandoId] = useState(null);

  const [filtroRaza, setFiltroRaza] = useState(null);
  const [filtroCategoria, setFiltroCategoria] = useState(null);
  const [filtroCabana, setFiltroCabana] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaAplicada, setBusquedaAplicada] = useState('');

  const [razas, setRazas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  // Un Select que carga de la API necesita su `loading`: sin esto el productor
  // abre el desplegable, lo ve vacío, lo cierra, y tiene que volver a abrirlo
  // cuando la API contesta. Uno solo para los dos, porque salen del mismo
  // `Promise.all` y terminan juntos.
  const [cargandoFiltros, setCargandoFiltros] = useState(true);

  // Cuántos animales quedaron afuera del listado por el tope de la rama
  // agrupada. Es estado y no un cálculo al vuelo porque `totalCandidatos` es la
  // suma de los `total` del servidor y `candidatos` lo que realmente llegó:
  // compararlos en el render escondería el aviso apenas cambia un filtro.
  const [candidatosTruncados, setCandidatosTruncados] = useState(0);

  // ── Pestaña "En el catálogo" ────────────────────────────────────────────
  const [enCatalogo, setEnCatalogo] = useState([]);
  const [loadingCat, setLoadingCat] = useState(false);
  const [ordenSucio, setOrdenSucio] = useState(false);
  const [guardandoOrden, setGuardandoOrden] = useState(false);

  // `tab` es el de arriba (diseño / animales) y `subTab` el de adentro de
  // Animales. Arranca en `estructura` porque lo primero que hace un productor
  // con un catalogo recien creado es darle forma, no elegir hacienda.
  const [tab, setTab] = useState('estructura');
  const [subTab, setSubTab] = useState('todos');

  // Cancelación de la request en vuelo. Sin esto, dos cargas rápidas (cambiar
  // de página y tipear en el buscador) pueden volver desordenadas y la lenta
  // pisar a la nueva: la tabla termina mostrando el resultado de un filtro que
  // el usuario ya cambió. Es la misma race que se arregló en la ficha.
  const abortRef = useRef(null);

  const puedeVer = tieneRecurso('catalogo_genetica');
  // `fotos_videos` y NO `catalogo_genetica`: el modal de presentacion escribe
  // con `PUT /imagenes/:id`, que va gateado con ese recurso. Es la TERCERA
  // dependencia cruzada de esta pantalla, junto con `ver_animales` (la pestaña
  // de candidatos) y `crud_animales` (el editor de "Nombre corto").
  const puedeFotos = tieneRecurso('fotos_videos');
  const activo = catalogos.find((c) => c.id_catalogo_genetica === idActivo) || null;
  // Una sola lectura, como en Micrositio: la usan el ancho de la columna y la
  // visibilidad de la barra de guardado, que siempre tienen que coincidir.
  const esTabAncho = TABS_ANCHOS.includes(tab);

  // Las cabañas de ESTE catálogo: el scope real de sus animales.
  //
  // La dependencia es una CLAVE PRIMITIVA y no `activo`, que es un `.find`
  // sobre `catalogos` y devuelve una referencia NUEVA cada vez que
  // `cargarCatalogos` reemplaza el array con el JSON recién parseado. Con
  // `[activo]`, ese memo se recalculaba, `cargarCandidatos` cambiaba de
  // identidad y su effect volvía a disparar: guardar la config —prender el
  // switch, cambiar la plantilla, salir del campo de WhatsApp— refetcheaba el
  // padrón entero, que no tiene nada que ver. En la rama agrupada eso es un
  // request POR CABAÑA de hasta 200 filas, contra una base en otra región.
  const claveCabanas = (activo?.cabanas || [])
    .map((c) => c.id_cabana).sort((a, b) => a - b).join(',');
  const idsCabanasActivo = useMemo(
    () => (claveCabanas ? claveCabanas.split(',').map(Number) : []),
    [claveCabanas],
  );
  const esAgrupado = idsCabanasActivo.length > 1;

  // ── Carga de los catálogos ──────────────────────────────────────────────
  // `recienCreado` es el catálogo ENTERO que devolvió el POST, y hace dos
  // cosas distintas.
  //
  // 1. LLEVA LA PANTALLA AHÍ. Sin esto, el primer catálogo se seleccionaba solo
  //    (porque `prev` era null) pero el SEGUNDO no: el modal se cerraba
  //    diciendo "Catálogo creado" y en pantalla no cambiaba nada.
  //
  // 2. LA PINTA AL INSTANTE. Crear disparaba una CADENA de cinco round-trips
  //    contra una base que está en otra región —POST, GET de la lista, GET de
  //    la config, GET de los animales y uno por cabaña de candidatos— y el
  //    modal se cerraba antes de que empezaran. El productor se quedaba cinco
  //    o seis segundos mirando el empty state que dice "todavía no tenés
  //    ningún catálogo", justo después de crear uno. El POST ya trae todo lo
  //    que esa pantalla necesita para dibujarse; volver a pedirlo era tirar el
  //    dato y pagar la espera dos veces.
  const cargarCatalogos = useCallback(async (recienCreado = null) => {
    if (!puedeVer) { setCargando(false); return; }
    // Pintar PRIMERO con lo que ya vino, después refrescar. La lista y el
    // límite hay que releerlos igual —cambiaron para todos los catálogos, no
    // sólo para éste— pero el usuario no tiene por qué esperarlos mirando algo
    // que ya es falso.
    if (recienCreado) {
      setCatalogos((prev) => {
        const yaEsta = prev.some(
          (c) => c.id_catalogo_genetica === recienCreado.id_catalogo_genetica,
        );
        return yaEsta ? prev : [...prev, recienCreado];
      });
      setIdActivo(recienCreado.id_catalogo_genetica);
      setConfig(recienCreado);
      setWhatsapp(recienCreado.whatsapp_consultas || '');
      setCanalConsultas(recienCreado.canal_consultas || 'whatsapp');
      setConfigSitio(recienCreado.configuracion_micrositio || {});
      setPlantilla(recienCreado.plantilla_index || 'op1');
      setSitioSucio(false);
    }

    setRefrescando(true);
    try {
      const { data } = await catalogoGeneticaService.getAll();
      setCatalogos(data.catalogos || []);
      setCabanas(data.cabanas || []);
      setLimite(data.limite || null);
      // Se elige el primero sólo si no había ninguno seleccionado: mantener la
      // selección al recargar evita que guardar algo te devuelva a otro catálogo.
      setIdActivo((prev) => {
        if (recienCreado) return recienCreado.id_catalogo_genetica;
        // Se mantiene la seleccion al recargar: guardar algo no puede
        // devolverte a otro catalogo.
        const sigueExistiendo = (data.catalogos || [])
          .some((c) => c.id_catalogo_genetica === prev);
        return sigueExistiendo ? prev : (data.catalogos?.[0]?.id_catalogo_genetica ?? null);
      });
    } catch (e) {
      message.error(e.response?.data?.error || 'No se pudieron cargar los catálogos');
    } finally {
      setCargando(false);
      setRefrescando(false);
    }
    // `message` (de App.useApp()) queda AFUERA de las deps porque no es un
    // dato del que dependa esta carga: es ESTABLE (antd memoiza el contexto
    // de `App`), asi que incluirla tampoco re-dispararia nada; se omite y se
    // silencia el linter para que la lista de deps diga solo lo que importa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeVer]);

  useEffect(() => { cargarCatalogos(); }, [cargarCatalogos]);

  // ── Config del catálogo activo ──────────────────────────────────────────
  useEffect(() => {
    if (!idActivo) { setConfig(null); return; }
    // Ya la tenemos: viene del POST del alta o de un PUT. Pedirla de nuevo es
    // un round-trip para traer exactamente lo que está en pantalla.
    if (config?.id_catalogo_genetica === idActivo) return;
    let vivo = true;
    (async () => {
      try {
        const { data } = await catalogoGeneticaService.get(idActivo);
        if (!vivo) return;
        setConfig(data);
        setWhatsapp(data.whatsapp_consultas || '');
        setCanalConsultas(data.canal_consultas || 'whatsapp');
        setConfigSitio(data.configuracion_micrositio || {});
        setPlantilla(data.plantilla_index || 'op1');
        setSitioSucio(false);
      } catch (e) {
        if (vivo) message.error(e.response?.data?.error || 'No se pudo cargar el catálogo');
      }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idActivo]);

  // Cambiar de catálogo resetea el contexto de las dos pestañas: selección,
  // paginado, TODOS los filtros y el estado del reorden. Sin esto lo de un
  // catálogo queda aplicado sobre el otro — y peor: se podrían marcar animales
  // del catálogo equivocado.
  //
  // Se resetean los cuatro filtros y no sólo el de cabaña. Antes el comentario
  // decía "los filtros" y limpiaba uno: en un código que trata a los
  // comentarios como contrato, uno que promete de más es una trampa para el
  // próximo, que lo lee y no vuelve a chequear.
  useEffect(() => {
    setSeleccion([]);
    setPagina(1);
    setFiltroCabana(null);
    setFiltroRaza(null);
    setFiltroCategoria(null);
    setBusqueda('');
    setBusquedaAplicada('');
    setOrdenSucio(false);
    setCandidatosTruncados(0);
  }, [idActivo]);

  // ── Catálogos de filtros ────────────────────────────────────────────────
  useEffect(() => {
    if (!puedeVer) return;
    let vivo = true;
    (async () => {
      try {
        // `getConAnimales` y no `getAll`: el catálogo de categorías es GLOBAL,
        // así que `getAll` ofrece categorías que este tenant no tiene cargadas y
        // filtrar por ellas devuelve cero. Un filtro que sólo puede vaciar la
        // tabla no es un filtro, es una trampa.
        const [r, c] = await Promise.all([
          razaService.getAll(),
          categoriaService.getConAnimales(),
        ]);
        if (!vivo) return;
        setRazas(r.data?.razas || r.data || []);
        setCategorias(c.data?.categorias || c.data || []);
      } catch {
        // AVISAR, aunque sea degradado. El silencio de antes se apoyaba en que
        // "los filtros son una ayuda": el problema es que al fallar el productor
        // pierde DOS funciones —filtrar por raza y por categoría— y le quedan
        // dos desplegables que parecen andar y devuelven vacío. Un control que
        // miente es peor que un control que no está.
        //
        // El texto dice qué se perdió y con qué seguir, que es lo que convierte
        // un error en algo accionable en vez de un susto.
        if (vivo) {
          message.error('No se pudieron cargar los filtros de raza y categoría. Podés buscar por nombre o RP.');
        }
      } finally {
        if (vivo) setCargandoFiltros(false);
      }
    })();
    return () => { vivo = false; };
    // `message` viene de `App.useApp()` y es ESTABLE (antd memoiza el
    // contexto de `App`): no re-dispararia nada en las deps, pero tampoco es
    // un dato del que dependa este effect. Se omite y se silencia el linter,
    // mismo criterio que la carga de catalogos de arriba.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeVer]);

  // ── Candidatos ──────────────────────────────────────────────────────────
  const cargarCandidatos = useCallback(async () => {
    // El abort va ANTES del guard, no después. Esta rama es justamente la que
    // vacía la tabla, así que dejar viva la request anterior significa que
    // cuando llegue —con `aborted` en false— vuelve a llenar la grilla que
    // acabamos de limpiar. Se ve al borrar el catálogo activo con una carga en
    // vuelo: la tabla se repuebla con animales de un catálogo que ya no existe,
    // con los checkboxes vivos, y `preserveSelectedRowKeys` se lleva esa
    // selección al catálogo siguiente.
    abortRef.current?.abort();

    if (!puedeVer || !idActivo || !idsCabanasActivo.length) {
      setCandidatos([]);
      setTotalCandidatos(0);
      // Se limpia TAMBIEN aca: sin esto, pasar de un catalogo agrupado y
      // truncado a uno sin cabañas deja el cartel "Faltan 217 animales"
      // colgado sobre una tabla vacia.
      setCandidatosTruncados(0);
      // EL QUE ABORTA, APAGA. El `finally` de la request en vuelo no lo hace:
      // ve `aborted === true` y no toca nada, a proposito. Asi que si esta rama
      // aborta y se va sin apagar, el spinner queda girando PARA SIEMPRE sobre
      // una tabla vacia — pasa al cambiar a un catalogo sin cabañas mientras
      // habia una carga en curso.
      setLoadingCand(false);
      return;
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoadingCand(true);
    try {
      // La cabaña SIEMPRE viaja como filtro: la del selector si el productor
      // eligió una, y si no la única del catálogo. Con un catálogo agrupado y
      // sin elegir, se piden las dos por separado y se juntan — `GET /animales`
      // filtra por UNA cabaña, no por un conjunto.
      const cabanasAPedir = filtroCabana ? [filtroCabana] : idsCabanasActivo;
      const comunes = {
        per_page: PER_PAGE,
        ...(filtroRaza ? { raza: filtroRaza } : {}),
        ...(filtroCategoria ? { categoria: filtroCategoria } : {}),
        ...(busquedaAplicada ? { search: busquedaAplicada } : {}),
      };

      if (cabanasAPedir.length === 1) {
        const { data } = await catalogoGeneticaService.getCandidatos(
          idCliente,
          { ...comunes, page: pagina, cabana: cabanasAPedir[0] },
          ctrl.signal,
        );
        if (ctrl.signal.aborted) return;
        setCandidatos(data.animales || []);
        setTotalCandidatos(data.total ?? (data.animales || []).length);
        setCandidatosTruncados(0);
      } else {
        // Catálogo agrupado sin filtro de cabaña. Se pide cada una y se
        // concatena, y la paginación pasa a ser del cliente sobre lo traído:
        // paginar en el servidor dos listas distintas y mezclarlas daría
        // páginas que se pisan.
        //
        // EL TOPE DE 200 POR CABAÑA SE AVISA, no se esconde. Un 200 con una
        // respuesta incompleta es la peor clase de falla porque no deja rastro:
        // la tabla diría "617 animales" mientras pagina sobre 400, y el
        // productor publicaría creyendo que vio todo el padrón. Mismo detector
        // que el de la navegación de la ficha (`AnimalForm`, `vista=ids`).
        const respuestas = await Promise.all(
          cabanasAPedir.map((idCab) => catalogoGeneticaService.getCandidatos(
            idCliente, { ...comunes, per_page: TOPE_POR_CABANA, page: 1, cabana: idCab }, ctrl.signal,
          )),
        );
        if (ctrl.signal.aborted) return;
        const todos = respuestas.flatMap((r) => r.data.animales || []);
        const total = respuestas.reduce((acc, r) => acc + (r.data.total || 0), 0);
        setCandidatos(todos);
        setTotalCandidatos(total);
        setCandidatosTruncados(Math.max(0, total - todos.length));
      }
    } catch (e) {
      if (e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
      message.error(e.response?.data?.error || 'No se pudieron cargar los animales');
    } finally {
      if (!ctrl.signal.aborted) setLoadingCand(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeVer, idActivo, idCliente, idsCabanasActivo, pagina, filtroRaza,
      filtroCategoria, filtroCabana, busquedaAplicada]);

  useEffect(() => { cargarCandidatos(); }, [cargarCandidatos]);
  useEffect(() => () => abortRef.current?.abort(), []);

  // ── Los que ya están en el catálogo ─────────────────────────────────────
  const cargarAnimalesDelCatalogo = useCallback(async () => {
    if (!puedeVer || !idActivo) { setEnCatalogo([]); return; }
    setLoadingCat(true);
    try {
      const { data } = await catalogoGeneticaService.getAnimales(idActivo);
      setEnCatalogo(data.animales || []);
      setOrdenSucio(false);
    } catch (e) {
      message.error(e.response?.data?.error || 'No se pudo cargar el catálogo');
    } finally {
      setLoadingCat(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeVer, idActivo]);

  useEffect(() => { cargarAnimalesDelCatalogo(); }, [cargarAnimalesDelCatalogo]);

  // ── Guardar configuración ───────────────────────────────────────────────
  const guardarConfig = async (cambios) => {
    if (!idActivo) return;
    setSavingConfig(true);
    try {
      const { data } = await catalogoGeneticaService.update(idActivo, cambios);
      setConfig(data.catalogo);
      setWhatsapp(data?.catalogo?.whatsapp_consultas || '');
      setCanalConsultas(data?.catalogo?.canal_consultas || 'whatsapp');
      message.success('Catálogo actualizado');
      // El listado guarda `activo` y el nombre, así que se recarga: si no, el
      // selector sigue diciendo "apagado" sobre un catálogo recién prendido.
      cargarCatalogos();
    } catch (e) {
      message.error(e.response?.data?.error || 'No se pudo guardar');
      // Se relee para no dejar la UI mostrando un valor que la base rechazó:
      // un switch que quedó prendido sobre un PUT fallido es una mentira.
      try {
        const { data } = await catalogoGeneticaService.get(idActivo);
        setConfig(data);
        setWhatsapp(data.whatsapp_consultas || '');
        setCanalConsultas(data.canal_consultas || 'whatsapp');
      } catch { /* si tampoco relee, el error ya se mostró */ }
    } finally {
      setSavingConfig(false);
    }
  };

  // Un cambio de diseño toca SOLO la memoria. Se persiste con "Guardar
  // cambios", como en Micrositio.
  const cambiarConfigSitio = (clave, valor) => {
    setConfigSitio((prev) => ({ ...prev, [clave]: valor }));
    setSitioSucio(true);
  };

  const guardarSitio = async () => {
    if (!idActivo) return;
    setSavingConfig(true);
    try {
      // Las DOS cosas en el mismo PUT: la config va en su JSON y la
      // plantilla como campo de primer nivel, que es donde el endpoint la
      // espera. Un solo botón guarda todo el diseño.
      const { data } = await catalogoGeneticaService.update(idActivo, {
        configuracion_micrositio: configSitio,
        plantilla_index: plantilla,
      });
      // Se relee lo que devolvió el backend y no lo que teníamos: `set_config()`
      // descarta las claves que no conoce, así que si el front mandó una de más
      // el estado local quedaría afirmando algo que la base no guardó.
      setConfig(data.catalogo);
      setConfigSitio(data?.catalogo?.configuracion_micrositio || {});
      setPlantilla(data?.catalogo?.plantilla_index || 'op1');
      setSitioSucio(false);
      message.success('Diseño guardado');
    } catch (e) {
      message.error(e.response?.data?.error || 'No se pudo guardar el diseño');
    } finally {
      setSavingConfig(false);
    }
  };

  // El hero del catálogo. `FondoHeroTab` sube por defecto a
  // `evento.imgsec_evento`, que acá no existe: la imagen vive dentro del JSON
  // de config (`hero_img`), así que se le inyecta cómo guardarla.
  //
  // El `configSitio` se sincroniza con lo que devuelve el backend en vez de
  // asumir la URL: si el disco falló, la respuesta no trae imagen y el estado
  // local no puede quedar afirmando que sí.
  const subirHero = async (file) => {
    const { data } = await catalogoGeneticaService.subirImagen(idActivo, file, 'hero_img');
    const url = data?.catalogo?.configuracion_micrositio?.hero_img || data?.url || null;
    if (url) {
      setConfigSitio((prev) => ({ ...prev, hero_img: url }));
      setConfig(data.catalogo);
      // `sitioSucio` NO se apaga: lo de la imagen quedó guardado, pero lo que
      // el productor tenga escrito sin guardar sigue sin guardar. Apagarlo le
      // diría que está todo listo cuando no lo está.
    }
    return url;
  };

  // Las imágenes de la config que NO son el hero: el logo de la cabecera y la
  // que se superpone al fondo. Mismo endpoint, distinto `campo` — la lista
  // blanca vive en el backend, así que mandar uno inventado es un 400 y no una
  // ruta de archivo metida adentro de un color.
  const subirImagenConfig = async (file, campo) => {
    const { data } = await catalogoGeneticaService.subirImagen(idActivo, file, campo);
    setConfig(data.catalogo);

    // SOLO SE TOCA LA CLAVE DE LA IMAGEN, y esto no es un detalle: antes se
    // reemplazaba `configSitio` entero por lo que devolvía el servidor, y eso
    // BORRABA lo que el productor había escrito y todavía no había guardado.
    //
    // El caso real: cargás el título de "Nuestra historia", escribís el
    // párrafo, subís la imagen — y el título y el párrafo desaparecen. Nadie
    // puede saber que subir una foto obliga a guardar antes. Se pierde trabajo
    // sin ningún aviso, que es la peor forma de perderlo.
    //
    // La URL sale de la respuesta y no se asume: si el disco falló, el backend
    // no la devuelve y el estado local no puede quedar afirmando que sí.
    const url = data?.catalogo?.configuracion_micrositio?.[campo] ?? data?.url ?? '';
    setConfigSitio((prev) => ({ ...prev, [campo]: url }));
    // `sitioSucio` NO se apaga: lo de la imagen ya está guardado, pero lo que
    // el productor tenga escrito sin guardar sigue sin guardar. Apagarlo le
    // diría que está todo listo cuando no lo está.
  };

  const quitarImagenConfig = async (campo) => {
    // Se manda la config LOCAL con la clave vacía, así lo que el productor
    // venía escribiendo se guarda junto en vez de perderse.
    const nueva = { ...configSitio, [campo]: '' };
    // LA PLANTILLA VIAJA TAMBIEN, igual que en `guardarSitio`. No es una clave
    // de `configSitio` sino una columna del modelo, asi que sin mandarla el PUT
    // ni se entera de que cambio. Y como despues de guardar se apaga
    // `sitioSucio`, la eleccion sin guardar del productor se perdia en silencio
    // con el boton Guardar deshabilitado avisando que estaba todo listo.
    const { data } = await catalogoGeneticaService.update(idActivo, {
      configuracion_micrositio: nueva,
      plantilla_index: plantilla,
    });
    setConfig(data.catalogo);
    setConfigSitio(data?.catalogo?.configuracion_micrositio || {});
    setSitioSucio(false);
  };

  // Borrar es dejar la clave vacía y guardar: no hace falta un endpoint propio,
  // el PUT de config ya sabe hacerlo.
  const eliminarHero = async () => {
    const nueva = { ...configSitio, hero_img: '' };
    // LA PLANTILLA VIAJA TAMBIEN, igual que en `guardarSitio`. No es una clave
    // de `configSitio` sino una columna del modelo, asi que sin mandarla el PUT
    // ni se entera de que cambio. Y como despues de guardar se apaga
    // `sitioSucio`, la eleccion sin guardar del productor se perdia en silencio
    // con el boton Guardar deshabilitado avisando que estaba todo listo.
    const { data } = await catalogoGeneticaService.update(idActivo, {
      configuracion_micrositio: nueva,
      plantilla_index: plantilla,
    });
    setConfig(data.catalogo);
    setConfigSitio(data?.catalogo?.configuracion_micrositio || {});
    setPlantilla(data?.catalogo?.plantilla_index || 'op1');
    setSitioSucio(false);
  };

  // El nombre de fantasía, editado en la tabla.
  //
  // Guarda al SALIR del campo y no en cada tecla: cada pulsación sería un PUT
  // contra una base que está en otra región. Y sólo si cambió — volver a
  // guardar lo mismo gasta un round-trip para nada.
  const guardarNombreCorto = async (animal, valor) => {
    const limpio = (valor || '').trim();
    if (limpio === (animal.nombre_corto || '')) return;
    try {
      await animalService.update(animal.id_animal, { nombre_corto: limpio });
      // Se parchea en memoria en vez de recargar las dos tablas: recargar
      // devolvería la lista al orden del servidor y, en la pestaña del
      // catálogo, se comería un reorden sin guardar.
      const parchear = (lista) => lista.map(
        (a) => (a.id_animal === animal.id_animal ? { ...a, nombre_corto: limpio } : a),
      );
      setCandidatos(parchear);
      setEnCatalogo(parchear);
    } catch (e) {
      message.error(e.response?.data?.error || 'No se pudo guardar el nombre corto');
    }
  };

  // ── Marcar / desmarcar en lote ──────────────────────────────────────────
  // `limpiarSeleccion` es explicito y por defecto NO limpia. Antes limpiaba
  // siempre, y el switch de "En venta" de la pestaña del catalogo pasa por aca:
  // el productor armaba una seleccion de 40 animales en la otra pestaña, tocaba
  // un switch, volvia y no habia nada. Peor que el bug era la contradiccion —
  // la pantalla PROMETE que la seleccion se mantiene y hasta ofrece un boton
  // para limpiarla a mano.
  const aplicarMarca = async (
    ids, cambios, textoOk, { idFila = null, limpiarSeleccion = false } = {},
  ) => {
    if (!ids.length || !idActivo) return;
    setMarcando(true);
    setMarcandoId(idFila);
    try {
      const { data } = await catalogoGeneticaService.marcar(idActivo, {
        animales: ids, ...cambios,
      });
      // `ignorados_por_ajenos` cuenta todo lo que el backend descartó: ajenos,
      // de otra cabaña, o sin cabaña ninguna. No debería llegar acá porque el
      // listado ya pide sólo lo publicable, pero si llega hay que decirlo:
      // callarlo deja al productor creyendo que publicó lo que no publicó.
      if (data.ignorados_por_ajenos > 0) {
        message.warning(
          `${data.ignorados_por_ajenos} animales quedaron afuera: no son propios `
          + 'o no pertenecen a las cabañas de este catálogo.',
          6,
        );
      }
      message.success(`${data.actualizados} ${textoOk}`);
      if (limpiarSeleccion) setSeleccion([]);

      // HAY UN REORDEN SIN GUARDAR y la operación toca SÓLO filas que ya
      // están en la tabla (el switch "En venta", "Sacar del catálogo"). Ahí se
      // parchea en memoria: recargar devolvería la lista al orden del servidor
      // y además apagaría `ordenSucio`, o sea que perdería quince movimientos
      // Y encima diría que está todo guardado. Es el mismo problema que
      // `limpiarSeleccion` unas líneas arriba: un efecto lateral que se come
      // trabajo del usuario.
      //
      // EL ATAJO SÓLO VALE SI NO ENTRA NADIE NUEVO. `.map()` y `.filter()`
      // trabajan sobre lo que ya está en el array, así que con los botones
      // "Agregar" —que pasan por esta misma función— los animales nuevos no
      // aparecían por ningún lado: el toast decía "10 agregados", la pestaña
      // seguía en (5), y "Guardar orden" mandaba 5 ids dejando a los otros 10
      // con `orden_catalogo` NULL. Cuando hay altas se recarga y se avisa que
      // el reorden se perdió, que es feo pero es verdad.
      const todosPresentes = ids.every(
        (idA) => enCatalogo.some((a) => a.id_animal === idA),
      );

      if (ordenSucio && todosPresentes) {
        const tocados = new Set(ids);
        setEnCatalogo((prev) => prev
          .map((a) => (tocados.has(a.id_animal) ? { ...a, ...cambios } : a))
          // Sacar del catalogo saca la fila de esta tabla, tambien en memoria.
          .filter((a) => cambios.muestra_en_catalogo !== 0 || !tocados.has(a.id_animal)));
        await cargarCandidatos();
      } else {
        if (ordenSucio) {
          message.warning(
            'Se recargó la lista para mostrar los animales nuevos, así que el '
            + 'orden que tenías sin guardar volvió al último guardado.',
            6,
          );
        }
        await Promise.all([cargarAnimalesDelCatalogo(), cargarCandidatos()]);
      }
      cargarCatalogos();
    } catch (e) {
      message.error(e.response?.data?.error || 'No se pudo aplicar el cambio');
    } finally {
      setMarcando(false);
      setMarcandoId(null);
    }
  };

  const sacarDelCatalogo = (animal) => {
    modal.confirm({
      title: '¿Sacar del catálogo?',
      content: (
        <>
          <Paragraph style={{ marginBottom: 8 }}>
            <strong>{animal.nom_animal || animal.rp_animal || `#${animal.id_animal}`}</strong> deja
            de mostrarse en tu catálogo público.
          </Paragraph>
          <Text type="secondary">
            Si estaba marcado como <em>En venta</em>, también se apaga: un animal en venta
            que no se muestra en ningún lado no se puede consultar ni corregir.
          </Text>
        </>
      ),
      okText: 'Sacar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () => aplicarMarca([animal.id_animal], { muestra_en_catalogo: 0 }, 'animal fuera del catálogo'),
    });
  };

  const borrarCatalogo = () => {
    if (!activo) return;
    modal.confirm({
      title: `¿Borrar "${activo.nombre_catalogo}"?`,
      content: (
        <>
          <Paragraph style={{ marginBottom: 8 }}>
            Se elimina la página y su dirección <Text code>{activo.slug}</Text> deja de funcionar.
          </Paragraph>
          <Text type="secondary">
            Los animales que elegiste NO se desmarcan: si volvés a crear un catálogo con
            las mismas cabañas, recuperás la selección.
          </Text>
        </>
      ),
      okText: 'Borrar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await catalogoGeneticaService.remove(activo.id_catalogo_genetica);
          message.success('Catálogo borrado');
          setIdActivo(null);
          cargarCatalogos();
        } catch (e) {
          message.error(e.response?.data?.error || 'No se pudo borrar');
        }
      },
    });
  };

  // ── Reordenar (local hasta que se guarda) ───────────────────────────────
  const mover = (index, destino) => {
    setEnCatalogo((prev) => {
      if (destino < 0 || destino >= prev.length) return prev;
      const copia = [...prev];
      const [fila] = copia.splice(index, 1);
      copia.splice(destino, 0, fila);
      return copia;
    });
    setOrdenSucio(true);
  };

  const guardarOrden = async () => {
    setGuardandoOrden(true);
    try {
      await catalogoGeneticaService.ordenar(idActivo, enCatalogo.map((a) => a.id_animal));
      message.success('Orden guardado');
      setOrdenSucio(false);
    } catch (e) {
      message.error(e.response?.data?.error || 'No se pudo guardar el orden');
    } finally {
      setGuardandoOrden(false);
    }
  };

  const urlPublica = config?.slug
    ? `${window.location.origin}/genetica/${config.slug}`
    : null;

  // ── Gates ───────────────────────────────────────────────────────────────
  if (!puedeVer) {
    return (
      <Alert
        type="warning"
        showIcon
        message="No tenés acceso al catálogo de genética"
        description="Este módulo depende de tu plan. Consultá con el administrador para habilitarlo."
      />
    );
  }

  if (cargando) return <PujaSpinner />;

  const encabezado = (
    <>
      <Title level={3}><ShopOutlined /> Catálogo de genética</Title>
      <Paragraph type="secondary">
        Tu vidriera permanente. No es un remate: no tiene fechas, está prendida o apagada,
        y los animales que elegís acá siguen pudiendo entrar y salir de remates sin que se pisen.
      </Paragraph>
    </>
  );

  const modalCatalogo = (
    <ModalCatalogo
      abierto={modalAbierto}
      onCerrar={() => { setModalAbierto(false); setEditando(null); }}
      onListo={cargarCatalogos}
      catalogo={editando}
      cabanas={cabanas}
    />
  );

  // ── Sin ningún catálogo todavía ─────────────────────────────────────────
  if (!catalogos.length) {
    return (
      <div>
        {encabezado}
        <Card>
          <Empty
            description={
              <Space direction="vertical" size={4}>
                <Text strong>Todavía no tenés ningún catálogo</Text>
                <Text type="secondary">
                  Un catálogo publica los animales de una o varias de tus cabañas,
                  en una página propia.
                </Text>
              </Space>
            }
          >
            {limite?.puede_crear ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalAbierto(true)}>
                Crear mi catálogo
              </Button>
            ) : (
              <Alert type="info" showIcon message={limite?.motivo || 'No podés crear catálogos'} />
            )}
          </Empty>
        </Card>
        {modalCatalogo}
      </div>
    );
  }

  // ── Tabla de candidatos ─────────────────────────────────────────────────
  const columnasCandidatos = [
    ...columnasBase(esAgrupado, guardarNombreCorto, setAnimalFotos, puedeFotos),
    {
      title: 'Estado',
      key: 'estado',
      width: 160,
      render: (_, a) => (
        a.muestra_en_catalogo
          ? (
            <Space size={4}>
              <Tag color="green">En catálogo</Tag>
              {a.apto_comercializar ? <Tag color="gold">En venta</Tag> : null}
            </Space>
          )
          : <Text type="secondary">—</Text>
      ),
    },
  ];

  // Con el catálogo agrupado y sin filtro de cabaña se trae todo junto y se
  // pagina del lado del cliente; en el resto de los casos pagina el servidor.
  const paginacionCliente = esAgrupado && !filtroCabana;

  const tabTodos = (
    <>
      <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
        {esAgrupado && (
          <Col xs={24} sm={12} md={5}>
            <Select
              allowClear
              style={{ width: '100%' }}
              placeholder="Cabaña"
              value={filtroCabana}
              onChange={(v) => { setFiltroCabana(v ?? null); setPagina(1); }}
              options={(activo?.cabanas || []).map((c) => ({
                value: c.id_cabana, label: c.nom_cabana,
              }))}
            />
          </Col>
        )}
        <Col xs={24} sm={12} md={esAgrupado ? 5 : 6}>
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder="Raza"
            loading={cargandoFiltros}
            value={filtroRaza}
            onChange={(v) => { setFiltroRaza(v ?? null); setPagina(1); }}
            options={razas.map((r) => ({ value: r.id_raza, label: r.nom_raza }))}
            showSearch
            optionFilterProp="label"
          />
        </Col>
        <Col xs={24} sm={12} md={esAgrupado ? 5 : 6}>
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder="Categoría"
            loading={cargandoFiltros}
            value={filtroCategoria}
            onChange={(v) => { setFiltroCategoria(v ?? null); setPagina(1); }}
            options={categorias.map((c) => ({ value: c.id_categoria, label: c.nom_categoria }))}
            showSearch
            optionFilterProp="label"
          />
        </Col>
        <Col xs={24} sm={16} md={esAgrupado ? 6 : 8}>
          <Input
            allowClear
            placeholder="Buscar por nombre o RP"
            prefix={<SearchOutlined />}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            onPressEnter={() => { setBusquedaAplicada(busqueda.trim()); setPagina(1); }}
            // Se busca al Enter o al salir del campo, no en cada tecla: cada
            // pulsación sería un request contra una base que está en otra región.
            onBlur={() => { setBusquedaAplicada(busqueda.trim()); setPagina(1); }}
          />
        </Col>
        <Col xs={24} sm={8} md={3}>
          <Button icon={<ReloadOutlined />} onClick={cargarCandidatos} block>
            Actualizar
          </Button>
        </Col>
      </Row>

      {candidatosTruncados > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Faltan ${candidatosTruncados} animales en esta lista`}
          description={
            'Con varias cabañas se traen hasta ' + TOPE_POR_CABANA + ' animales por cada una. '
            + 'Filtrá por cabaña, raza o categoría para verlos todos antes de elegir.'
          }
        />
      )}

      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {seleccion.length > 0
            // Dice "y de filtros" porque `preserveSelectedRowKeys` mantiene la
            // selección TAMBIÉN al cambiar de cabaña, raza o búsqueda, no sólo
            // de página. Sin decirlo, el productor filtra Mercedes y marca 10,
            // filtra Quimilí y marca 5, y aprieta "Agregar (15)" creyendo que
            // agrega 5. Se mantiene a propósito —elegir en varias tandas es
            // legítimo— pero entonces hay que decirlo y dejar limpiarla.
            ? `${seleccion.length} seleccionados en total (la selección se mantiene al cambiar de página y de filtros)`
            : 'Sólo aparecen animales propios de las cabañas de este catálogo. '
              + 'La genética consignada por cabañas invitadas se puede gestionar, pero no publicar como propia.'}
        </Text>
        <Space wrap>
          {seleccion.length > 0 && (
            <Button onClick={() => setSeleccion([])}>Limpiar selección</Button>
          )}
          <Button
            type="primary"
            icon={<EyeOutlined />}
            // `marcandoId` distingue QUE accion esta corriendo, igual que en
            // las filas: con `marcando` a secas los dos botones giraban ante
            // cualquiera de las dos.
            loading={marcandoId === 'agregar'}
            disabled={seleccion.length === 0 || marcando}
            onClick={() => aplicarMarca(
              seleccion,
              { muestra_en_catalogo: 1 },
              'animales agregados al catálogo',
              { limpiarSeleccion: true, idFila: 'agregar' },
            )}
          >
            Agregar al catálogo{seleccion.length > 0 ? ` (${seleccion.length})` : ''}
          </Button>
          <Button
            icon={<DollarOutlined />}
            loading={marcandoId === 'agregar-venta'}
            disabled={seleccion.length === 0 || marcando}
            onClick={() => aplicarMarca(
              seleccion,
              { muestra_en_catalogo: 1, apto_comercializar: 1 },
              'animales en catálogo y en venta',
              { limpiarSeleccion: true, idFila: 'agregar-venta' },
            )}
          >
            Agregar y marcar en venta
          </Button>
        </Space>
      </div>

      <Table
        scroll={{ x: 'max-content' }}
        rowKey="id_animal"
        dataSource={candidatos}
        columns={columnasCandidatos}
        rowSelection={{
          selectedRowKeys: seleccion,
          onChange: setSeleccion,
          // Sin esto la selección se pierde al pasar de página, porque antd sólo
          // conserva las claves cuyas filas están en `dataSource` — y con
          // paginación de servidor esas filas ya no están.
          preserveSelectedRowKeys: true,
          selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
        }}
        loading={loadingCand}
        size="small"
        pagination={paginacionCliente ? {
          pageSize: PER_PAGE,
          showSizeChanger: false,
          showTotal: (t) => `${t} animales`,
        } : {
          current: pagina,
          pageSize: PER_PAGE,
          total: totalCandidatos,
          onChange: setPagina,
          showSizeChanger: false,
          showTotal: (t) => `${t} animales`,
        }}
        locale={{ emptyText: <Empty description="No hay animales que coincidan con los filtros" /> }}
      />
    </>
  );

  // ── Tabla del catálogo ──────────────────────────────────────────────────
  const columnasCatalogo = [
    {
      title: 'Orden',
      key: 'orden',
      width: 120,
      render: (_, __, index) => (
        <Space size={2}>
          <Tooltip title="Al principio">
            <Button
              size="small" type="text" icon={<VerticalAlignTopOutlined />}
              disabled={index === 0} onClick={() => mover(index, 0)}
            />
          </Tooltip>
          <Button
            size="small" type="text" icon={<ArrowUpOutlined />}
            disabled={index === 0} onClick={() => mover(index, index - 1)}
          />
          <Button
            size="small" type="text" icon={<ArrowDownOutlined />}
            disabled={index === enCatalogo.length - 1} onClick={() => mover(index, index + 1)}
          />
        </Space>
      ),
    },
    ...columnasBase(esAgrupado, guardarNombreCorto, setAnimalFotos, puedeFotos),
    {
      title: 'En venta',
      key: 'apto',
      width: 110,
      render: (_, a) => (
        <Switch
          size="small"
          checked={!!a.apto_comercializar}
          loading={marcandoId === a.id_animal}
          disabled={marcando && marcandoId !== a.id_animal}
          onChange={(v) => aplicarMarca(
            [a.id_animal],
            { apto_comercializar: v ? 1 : 0 },
            v ? 'animal marcado en venta' : 'animal fuera de venta',
            { idFila: a.id_animal },
          )}
        />
      ),
    },
    {
      title: '',
      key: 'acciones',
      width: 60,
      render: (_, a) => (
        <Tooltip title="Sacar del catálogo">
          <Button danger type="text" size="small" icon={<DeleteOutlined />} onClick={() => sacarDelCatalogo(a)} />
        </Tooltip>
      ),
    },
  ];

  const tabCatalogo = (
    <>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Este es el orden real de tu catálogo público: los primeros son los que entran al
          index antes del «Ver todos».
          {esAgrupado && ` Las ${idsCabanasActivo.length} cabañas comparten una sola secuencia, porque es una sola página.`}
        </Text>
        <Space>
          {ordenSucio && <Text type="warning">Orden sin guardar</Text>}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={guardandoOrden}
            disabled={!ordenSucio}
            onClick={guardarOrden}
          >
            Guardar orden
          </Button>
        </Space>
      </div>

      <Table
        scroll={{ x: 'max-content' }}
        rowKey="id_animal"
        dataSource={enCatalogo}
        columns={columnasCatalogo}
        loading={loadingCat}
        size="small"
        // Sin paginar a propósito: el orden es de la lista COMPLETA y mover un
        // animal "al principio" desde la página 3 no tendría a dónde ir si la
        // tabla sólo conociera 20 filas.
        pagination={false}
        locale={{
          emptyText: (
            <Empty description="Todavía no elegiste ningún animal para este catálogo">
              <Button type="primary" onClick={() => setSubTab('todos')}>Elegir animales</Button>
            </Empty>
          ),
        }}
      />
    </>
  );

  return (
    <div>
      {encabezado}

      {/* Selector de catálogo. Con uno solo igual se muestra, porque es donde
          viven "editar cabañas" y "borrar" — esconderlo obligaría a poner esos
          botones en otro lado según cuántos catálogos haya. */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle" justify="space-between">
          <Col xs={24} md={12}>
            <Space wrap>
              <Select
                style={{ minWidth: 240 }}
                value={idActivo}
                // El Select muestra su propio spinner mientras se refresca la
                // lista. No se usa `PujaSpinner` para esto: ese desmonta la
                // pantalla entera, y hacer desaparecer lo que el productor
                // esta mirando para avisarle que algo se esta actualizando es
                // peor que no avisarle.
                loading={refrescando}
                onChange={setIdActivo}
                options={catalogos.map((c) => ({
                  value: c.id_catalogo_genetica,
                  label: (
                    <Space>
                      <span>{c.nombre_catalogo}</span>
                      <Tag color={c.activo ? 'green' : 'default'}>
                        {c.activo ? 'Activo' : 'Apagado'}
                      </Tag>
                    </Space>
                  ),
                }))}
              />
              {activo && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <BankOutlined />{' '}
                  {(activo.cabanas || []).map((c) => c.nom_cabana).join(' + ') || 'sin cabañas'}
                </Text>
              )}
            </Space>
          </Col>
          <Col xs={24} md={12} style={{ textAlign: 'right' }}>
            <Space wrap>
              <Button
                icon={<EditOutlined />}
                onClick={() => { setEditando(activo); setModalAbierto(true); }}
                disabled={!activo}
              >
                Cabañas y nombre
              </Button>
              {/* El <span> NO es decorativo. antd 5 sacó el wrapper que v4
                  ponía solo para esto, y un <button disabled> nativo no emite
                  `mouseenter`: sin el span el tooltip no abre NUNCA. Y lo que
                  se pierde es justo lo que importa — `limites_catalogo.py`
                  redacta tres motivos distintos (plan agotado, sin cabañas,
                  todas ya publicadas) y el productor veria un boton gris y
                  ninguna explicacion. */}
              <Tooltip title={limite?.puede_crear ? '' : limite?.motivo}>
                <span>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => { setEditando(null); setModalAbierto(true); }}
                    disabled={!limite?.puede_crear}
                  >
                    Nuevo catálogo
                  </Button>
                </span>
              </Tooltip>
              <Button danger icon={<DeleteOutlined />} onClick={borrarCatalogo} disabled={!activo}>
                Borrar
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {!activo ? null : (
        <>
          {/* ESTADO, no diseño. Prender/apagar y la dirección pública son
              acciones sueltas, así que se guardan al toque; el diseño se edita
              en los tabs y se persiste con su botón. Mezclarlos haría que
              arrastrar el slider del logo dispare un PUT por cada pixel. */}
          <Card size="small" style={{ marginBottom: 16 }}>
            {/* `align="top"` y no `middle`: la columna del canal es más alta
                por su texto de ayuda, y centrar verticalmente dejaba al switch
                y al número flotando a media altura contra ella. Los títulos de
                las tres columnas tienen que arrancar en la misma línea. */}
            <Row gutter={[24, 12]} align="top">
              <Col xs={24} md={8}>
                <Space direction="vertical" size={2}>
                  <Text strong>Catálogo público</Text>
                  <Space>
                    <Switch
                      checked={!!config?.activo}
                      loading={savingConfig}
                      onChange={(v) => guardarConfig({ activo: v ? 1 : 0 })}
                      checkedChildren="Activo"
                      unCheckedChildren="Apagado"
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {config?.activo ? 'Visible para cualquiera' : 'Nadie lo ve todavía'}
                    </Text>
                  </Space>
                </Space>
              </Col>

              <Col xs={24} md={8}>
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  <Text strong>WhatsApp de consultas</Text>
                  <Input
                    placeholder="Ej: 5493764123456"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    onBlur={() => {
                      const limpio = whatsapp.trim();
                      if (limpio !== (config?.whatsapp_consultas || '')) {
                        guardarConfig({ whatsapp_consultas: limpio });
                      }
                    }}
                    onPressEnter={(e) => e.target.blur()}
                  />
                </Space>
              </Col>

              <Col xs={24} md={8}>
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  <Text strong>Las consultas van por</Text>
                  <Select
                    style={{ width: '100%' }}
                    value={canalConsultas}
                    onChange={(v) => {
                      setCanalConsultas(v);
                      guardarConfig({ canal_consultas: v });
                    }}
                    options={[
                      { value: 'whatsapp', label: 'WhatsApp' },
                      { value: 'interna', label: 'Mensajería interna' },
                    ]}
                  />
                  {/* El texto de ayuda cambia con la eleccion: es la unica
                      forma de que el productor entienda QUE pasa del lado del
                      comprador antes de elegir, sin tener que ir a probarlo en
                      la ficha publica. */}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {canalConsultas === 'whatsapp'
                      ? 'La consulta queda registrada en el panel Y abre el WhatsApp del comprador con el número de arriba. Sin número cargado, el botón no se muestra en la ficha.'
                      : 'La consulta queda solo en el panel — la respondés desde "Consultas del catálogo".'}
                  </Text>
                </Space>
              </Col>

              {urlPublica && (
                <Col xs={24} md={8}>
                  {/* VUELVE EL BOTON DE COPIAR. Se habia sacado porque la ruta
                      publica `/genetica/:slug` no existia y ofrecer "Copiar"
                      entregaba un link que caia en el login. Ya existe y sirve
                      contenido, asi que el link es real y el boton corresponde.
                      El `copyable` de antd y no `navigator.clipboard` a mano:
                      en contexto inseguro el acceso a `clipboard` tira un
                      TypeError SINCRONO que ningun `.catch()` agarra. */}
                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Space size={6}>
                      <LinkOutlined />
                      <Text strong>Dirección</Text>
                    </Space>
                    <Text
                      code
                      type="secondary"
                      style={{ fontSize: 12 }}
                      copyable={{ text: urlPublica, tooltips: ['Copiar el link', 'Copiado'] }}
                    >
                      {urlPublica}
                    </Text>
                    {/* El texto de ayuda dice la verdad segun el estado, que es
                        lo que el cartel anterior hacia mal: avisaba que "no
                        funciona" aun con el catalogo prendido. Apagado responde
                        404 a proposito, no 403 — ver el contrato del backend. */}
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {config?.activo
                        ? 'Cualquiera con el link puede verlo.'
                        : 'El catálogo está apagado: el link responde 404 hasta que lo actives.'}
                    </Text>
                  </Space>
                </Col>
              )}
            </Row>
          </Card>

          {/* Barra de acciones ARRIBA y no dentro del Tabs. Con la barra
              lateral, el `tabBarExtraContent` cae debajo del menú vertical, o
              sea lejos de lo que guarda y en un lugar distinto según cuántas
              secciones haya. */}
          {!esTabAncho && (
            <Row justify="end" style={{ marginBottom: 12 }}>
              <Space>
                {sitioSucio && <Text type="warning">Cambios sin guardar</Text>}
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={savingConfig}
                  disabled={!sitioSucio}
                  onClick={guardarSitio}
                >
                  Guardar cambios
                </Button>
              </Space>
            </Row>
          )}

          <Card styles={{ body: { paddingTop: 12 } }}>
            <Tabs
              activeKey={tab}
              onChange={setTab}
              // LA MISMA navegación que Micrositio: menú vertical a la
              // izquierda. Configurar el sitio de un remate y el de un catálogo
              // son la misma tarea; con dos disposiciones distintas el
              // productor la aprende dos veces.
              tabPosition="left"
              items={[
                {
                  key: 'estructura',
                  label: <span><LayoutOutlined /> Estructura</span>,
                  children: (
                    <>
                      {/* Lo PROPIO del catálogo primero —qué plantilla y qué
                          secciones— y después la cabecera y el pie, que son
                          los mismos que en un remate. En Micrositio esto es un
                          solo tab: separarlo en dos era una sección de más que
                          allá no existe, y todo esto se hace justamente para
                          que el productor lo aprenda una sola vez. */}
                      <EstructuraCatalogoTab
                        config={configSitio}
                        onChange={cambiarConfigSitio}
                        plantilla={plantilla}
                        onPlantillaChange={(v) => {
                          setPlantilla(v);
                          setSitioSucio(true);
                        }}
                        onSubirImagen={subirImagenConfig}
                        onQuitarImagen={quitarImagenConfig}
                      />
                      <Divider />
                      <EstructuraTab
                        config={configSitio}
                        onChange={cambiarConfigSitio}
                        // El catálogo no tiene barra de filtros lateral: el
                        // suyo vive dentro de la sección de hembras y se
                        // prende más arriba, en esta misma pantalla.
                        // `genealogia_vista`/`deps_vista` son de la ficha del
                        // REMATE: el catálogo tiene su propia presentación
                        // fija (barra de percentil) y no las lee.
                        ocultar={['filtro_posicion', 'genealogia_vista', 'deps_vista', 'termino_lote']}
                      />
                    </>
                  ),
                },
                {
                  key: 'contenidos',
                  label: <span><EditOutlined /> Textos y links</span>,
                  children: (
                    <ContenidosTab
                      config={configSitio}
                      onChange={cambiarConfigSitio}
                      onSubirImagen={subirImagenConfig}
                      onQuitarImagen={quitarImagenConfig}
                    />
                  ),
                },
                {
                  key: 'colores',
                  label: <span><BgColorsOutlined /> Colores</span>,
                  children: (
                    <ColoresTab config={configSitio} onChange={cambiarConfigSitio} />
                  ),
                },
                {
                  key: 'tipografias',
                  label: <span><FontSizeOutlined /> Tipografías</span>,
                  children: (
                    <TipografiasTab config={configSitio} onChange={cambiarConfigSitio} />
                  ),
                },
                {
                  key: 'hero',
                  label: <span><PictureOutlined /> Hero Zone</span>,
                  children: (
                    <FondoHeroTab
                      config={configSitio}
                      onChange={cambiarConfigSitio}
                      // `hero_margen_superior` es del micrositio de REMATE: no
                      // esta en `DEFAULT_CATALOGO_CONFIG` y `set_config()` la
                      // descartaria en silencio. Mismo motivo que
                      // `filtro_posicion` en `EstructuraTab`.
                      ocultar={['hero_margen_superior']}
                      imagenActual={configSitio.hero_img || null}
                      subirImagen={subirHero}
                      eliminarImagen={eliminarHero}
                    />
                  ),
                },
                {
                  key: 'contacto',
                  label: <span><ContactsOutlined /> Contacto</span>,
                  children: (
                    <ContactoTab config={configSitio} onChange={cambiarConfigSitio} />
                  ),
                },
                {
                  key: 'animales',
                  label: <span><TeamOutlined /> Animales ({enCatalogo.length})</span>,
                  children: (
                    <Tabs
                      activeKey={subTab}
                      onChange={setSubTab}
                      items={[
                        {
                          key: 'todos',
                          // Con la rama agrupada truncada, `totalCandidatos` (la
                          // suma del servidor) y las filas que realmente llegaron
                          // NO son lo mismo. Se muestran las DOS: quedarse con una
                          // sola, sea cual sea, contradice a la otra en algun otro
                          // lugar de la pantalla.
                          label: `Disponibles${totalCandidatos
                            ? (candidatosTruncados > 0
                              ? ` (${candidatos.length} de ${totalCandidatos})`
                              : ` (${totalCandidatos})`)
                            : ''}`,
                          children: tabTodos,
                        },
                        {
                          key: 'catalogo',
                          label: `En el catálogo (${enCatalogo.length})`,
                          children: tabCatalogo,
                        },
                      ]}
                    />
                  ),
                },
              ]}
            />
          </Card>
        </>
      )}

      {modalCatalogo}

      {/* Las fotos del animal y como se presentan. `animalFotos` manda: null es
          cerrado.

          SE PARCHEA EN MEMORIA Y NO SE RECARGA, por el mismo motivo que
          `guardarNombreCorto`: recargar devuelve la lista al orden del servidor
          y apaga `ordenSucio`, asi que el productor que movio quince animales,
          abrio las fotos de uno y marco una portada perdia el reorden — y el
          boton de guardar se apagaba, como si estuviera todo guardado.

          Se parchean las DOS listas: la miniatura de la fila sale de
          `imagenes`, y la pestaña "Disponibles" muestra los mismos animales. */}
      <ModalPresentacionFotos
        animal={animalFotos}
        abierto={Boolean(animalFotos)}
        onCerrar={() => setAnimalFotos(null)}
        onCambio={(imagenes) => {
          const parchear = (lista) => lista.map(
            (a) => (a.id_animal === animalFotos?.id_animal ? { ...a, imagenes } : a),
          );
          setCandidatos(parchear);
          setEnCatalogo(parchear);
        }}
      />
    </div>
  );
};

export default CatalogoGenetica;
